import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { clamp } from "@/hooks/useIsMobile";

const WindowContext = createContext(null);
export const useWindows = () => useContext(WindowContext);

function buildState(apps) {
  const state = {};
  apps.forEach((a) => {
    state[a.id] = {
      open: a.id === "readme",
      minimized: false,
      maximized: false,
      z: a.id === "readme" ? 1 : 0,
      pos: { ...a.pos },
      size: { ...a.size },
    };
  });
  return state;
}

export function WindowProvider({ apps, children }) {
  const [wins, setWins] = useState(() => buildState(apps));
  const [topZ, setTopZ] = useState(1);
  const zRef = useRef(1);
  const appsRef = useRef(apps);

  const bumpZ = useCallback((id) => {
    zRef.current += 1;
    const z = zRef.current;
    setTopZ(z);
    setWins((w) => ({ ...w, [id]: { ...w[id], z } }));
  }, []);

  const open = useCallback(
    (id) => {
      setWins((w) => {
        const next = { ...w };
        if (window.matchMedia("(max-width: 767px)").matches) {
          Object.keys(next).forEach((k) => {
            next[k] = { ...next[k], open: k === id, minimized: false };
          });
        }
        const size = { ...next[id].size };
        const maxX = Math.max(16, window.innerWidth - size.w - 24);
        const maxY = Math.max(46, window.innerHeight - size.h - 130);
        next[id] = {
          ...next[id],
          open: true,
          minimized: false,
          pos: {
            x: clamp(next[id].pos.x, 16, maxX),
            y: clamp(next[id].pos.y, 46, maxY),
          },
        };
        return next;
      });
      bumpZ(id);
    },
    [bumpZ]
  );

  const close = useCallback((id) => {
    setWins((w) => ({ ...w, [id]: { ...w[id], open: false, minimized: false } }));
  }, []);

  const minimize = useCallback((id) => {
    setWins((w) => ({ ...w, [id]: { ...w[id], minimized: true } }));
  }, []);

  const toggleMax = useCallback((id) => {
    setWins((w) => ({ ...w, [id]: { ...w[id], maximized: !w[id].maximized } }));
  }, []);

  const focus = useCallback((id) => {
    setWins((w) => {
      if (!w[id]) return w;
      return w[id].z === zRef.current ? w : w;
    });
    bumpZ(id);
  }, [bumpZ]);

  const move = useCallback((id, pos) => {
    setWins((w) => ({ ...w, [id]: { ...w[id], pos } }));
  }, []);

  const resize = useCallback((id, size) => {
    setWins((w) => ({ ...w, [id]: { ...w[id], size } }));
  }, []);

  const value = useMemo(
    () => ({ wins, topZ, open, close, minimize, toggleMax, focus, move, resize }),
    [wins, topZ, open, close, minimize, toggleMax, focus, move, resize]
  );

  return <WindowContext.Provider value={value}>{children}</WindowContext.Provider>;
}
